// @flow
import _ from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import store from '../../store';

import {
    OVERVIEW_PANEL_CHANGE_CURRENT,
    OVERVIEW_PANEL_GET_CURRENT,
    OVERVIEW_PANEL_IS_VISIBLE
} from '../../store/modules/overview-panel/types';
import api from '../api';
import DataTable from '../core/charts/Datatable';
import dataBoard from '../core/databoard';
import filters from '../core/filters';
import worldMap from '../core/map';
import translate from '../core/tools/translate';
import environment from '../environment';
import layers from '../layers';
import { GeoJSONLayer } from '../layers/Leaflet/VectorLayer/GeojsonLayer';
import { CoreControl } from './CoreControl';
import { VectorType } from './VectorType';
import { ContextEnum } from '../../enuns/context.enum';
import { SaleTypeEnum } from '../../enuns/saleType.enum';
import { notify, NOTIFY_TYPE } from '../shared';
import { FeaturePermission } from '../../constants/feature.permission.helper';
// import 'mapbox.js';

declare var L: any;

const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

export class CustomersControl extends CoreControl {
    constructor() {
        super({ name: "Customers", context: "LAYER_CUSTOMER" });

        (this: any)._change = this._change.bind(this);
        (this: any)._changePeriod = this._changePeriod.bind(this);
        (this: any)._filterCallback = this._filterCallback.bind(this);
        (this: any)._purgeLayer = this._purgeLayer.bind(this);
        (this: any).createFilter = this.createFilter.bind(this);
        (this: any).toggleOverview = this.toggleOverview.bind(this);
        (this: any).currentStores = new Set();
    }

    static getInstance() {
        if (!CustomersControl._instance) {
            CustomersControl._instance = new CustomersControl();
        }

        return CustomersControl._instance;
    }

    init() {
        this.selecteds = [];
        this.colors = [
            "black",
            "yellow",
            "blue",
            "orange",
            "purple",
            "red",
            "teal",
            "pink",
            "brown",
            "gray",
        ];

        const values = {
            help: translate(`Help.${this.options.name}`),
            id: "layer-customers",
            input: "checkbox",
            label: translate(`Common.${this.options.name}`),
            layer: this.getLayer,
            change: this._change,
            filter: {
                enable: true,
                click: this.createFilter
            },
            name: this.options.name,
            purge: this._purgeLayer,
            overview: this.toggleOverview,
            type: "vector-points",
            links: {}
        };

        const formatDate = (dt: Date): string => moment(dt).format("MMM/YYYY");
        const {start, end} = this.getDate();

        values.links[translate("Action.ShowDateFrom")] = {
            id: "layer-customers-date",
            name: "filterYear",
            text: `${formatDate(start)} -> ${formatDate(end)}`,
            value: {
                start: start.toDate(),
                end: end.toDate()
            },
            change: this._changePeriod,
            type: "datepicker"
        };

        if (!this._instance) {
            this._instance = new VectorType(values);
        }
    }

    _resetCurrentCustomers() {
        this.getLayer().reset();
    }

    _change(e: any) {
        const storeLayer = layers.get("Dealerships", worldMap.getCurrent());

        const layer = this.getLayer();

        if (e.checked) {
            layer.redraw();
        } else {
            this.currentStores.forEach(store =>
                this._restoreStoreIcon(store, storeLayer)
            );
            layer.hide();

            const { symbology } = layer.options;
            layer.setOptions({ symbology: { ...symbology, values: [] } });

            this.selecteds = [];
            storeLayer.state.customIcons = [];
            storeLayer.reload();

            if (!storeLayer.visible()) {
                setTimeout(() => storeLayer.hide(), 500);
            }
        }

        this._setFilterButtonVisibility(e.checked);
    }

    _setFilterButtonVisibility(state: boolean) {
        try {
            this.getControls().button.overview.input.enable(state);
            this.getControls().button.overview.enable = state;

            if (
                store.getters[OVERVIEW_PANEL_GET_CURRENT] ===
                    this.options.name &&
                !state
            ) {
                dataBoard.hide();
                delete this._overviewFeatures;
            }
        } catch (err) {
            console.log(err);
        }

        state ? this.createFilter() : filters.close();
    }

    _purgeLayer() {
        const layer = this.getLayer();
        const storeLayer = layers.get("Dealerships", worldMap.getCurrent());

        if (this._instance.check()) {
            this.currentStores.forEach(store => {
                this._restoreStoreIcon(store, storeLayer);
            });
            this._updateFiltersValues(null);
            this._resetCurrentCustomers();
        }

        this.currentStores.clear();

        const { symbology } = this.getLayer().options;
        this.getLayer().setOptions({ symbology: { ...symbology, values: [] } });

        if (layer.visible()) {
            layer.reload();
        }
    }

    /**
     * Changes de style of current store icon
     * @param id
     * @returns {{store, customers: {value: number, style: {color, icon: {iconUrl: string, iconSize: [number,number], iconAnchor: [number,number]}, clickable: boolean}}}}
     * @private
     */
    _getIconStyle(id: number) {
        const color = this.colors.shift();
        this.colors.push(color);
        return {
            store: L.icon({
                iconUrl: `${environment.get('IMAGES_HOST')}/pin_store_customers_${color}.png`,
                iconSize: [50, 64], // size of the icon
                iconAnchor: [25, 60], // point of the icon which will correspond to marker's location
                labelAnchor: [25, -32]
            }),
            customers: {
                value: id,
                style: {
                    color,
                    icon: {
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_client_${color}.png`,
                        iconSize: [18, 18],
                        iconAnchor: [9, 9]
                    },
                    clickable: false
                }
            }
        };
    }

    _changePeriod(date: any, id: number = this.getLayer().getMap()._id) {
        this.date.set(id, {
            start: moment(date.start),
            end: moment(date.end),
            min: new Date(date.min),
            max: new Date(date.max)
        });

        if (this._instance.check()) {
            this.add([...this.currentStores]);
        }
    }

    get layerPeriod(){
        const id = this.getLayer().getMap()._id;
        return (this: any).date.get(id);
    }

    _setStoreIcon(id: number, storeLayer: GeoJSONLayer) {
        const style = this._getIconStyle(id);

        // change pin from the selected store
        storeLayer.changeIcon(id, style.store);
        return style;
    }

    _restoreStoreIcon(id: number, storeLayer: GeoJSONLayer) {
        const layer = this.getLayer();

        storeLayer.state.customIcons = [...storeLayer.state.customIcons.filter(x => x.idStore !== id)];

        storeLayer.restoreIcon(id);

        const { symbology } = layer.options;
        symbology.values = symbology.values.filter(s => String(s.value) !== String(id));

        layer.setOptions({ symbology });
    }

    _filterCallback(values: any) {
        this.check(true);
        this.add(values.dealers.value, values.fuelTypes, values.segments, values.saleType);
        api.general.log.action(ContextEnum.LayerCustomer, 'Filtro', JSON.stringify(values));
    }

    _updateFiltersValues(id: number, stores: any) {
        const currentMap = worldMap.getCurrent();

        const filterValues = _.cloneDeep(
            filters.get(
                `${_.kebabCase(this.options.name)}-filter-id`,
                currentMap._id
            )
        );

        const values = _.size(filterValues)
            ? filterValues
            : {
                dealers: {
                    metadata: {
                        name: 'dealers',
                        text: 'dealerName',
                        value: 'dealerId',
                        properties: [
                            'dealerName',
                            'dealerId',
                            'groupName',
                            'groupId'
                        ],
                        all: true
                    },
                    text: [],
                    value: []
                },
                groups: {
                    metadata: {
                        name: 'groups',
                        text: 'groupName',
                        value: 'groupId',
                        properties: [
                            'dealerName',
                            'dealerId',
                            'groupName',
                            'groupId'
                        ]
                    },
                    text: [],
                    value: []
                },
                ...(this.canSeeGroupClient()
                    ? {
                        groupClient: {
                            metadata: {
                                name: 'groupClient',
                                text: 'text',
                                value: 'value',
                                properties: [
                                    'text', 
                                    'value'
                                ],
                                all: true,
                            },
                            text: [],
                            value: [],
                        },
                    }
                    : {}),
            };

        delete values.dealers.metadata.all;
        const filteredStores = _.filter(
            environment.get("CONFIG.FILTERS.DEALERS_STORES"),
            s => s.Id === id || (stores && stores.has(s.Id))
        );
        values.dealers.text = _.map(filteredStores, s => s.Nome);
        values.dealers.value = _.map(filteredStores, s => s.Id);
        values.groups.text = _(filteredStores)
            .map(s => s.Grupo)
            .uniq()
            .value();
        values.groups.value = _(filteredStores)
            .map(s => s.Grupo)
            .uniq()
            .value();

        filters.set(
            `${_.kebabCase(this.options.name)}-filter-id`,
            currentMap._id,
            values
        );

        if (values.groupClient) {
            values.groupClient.text = _.map(filteredStores, g => g.text);
            values.groupClient.value = _.map(filteredStores, g => g.value);
        }

    }

    createFilter() {
        filters.create({
            id: `${_.kebabCase(this.options.name)}-filter-id`,
            label: translate(`Common.${this.options.name}`),
            type: _.lowerCase(this.options.name),
            onSubmit: this._filterCallback,
            fields: {
                fuelTypes: true,
                segments: true,
                saleType: true,
            },
        });
    }

    getDate(id: number = this.getLayer().getMap()._id) {
        if (_.isUndefined(this.date)) {
            this.date = new Map();
        }

        if (!this.date.has(id)) {
                this.date.set(id, id == 0 ? { start: moment(environment.get("CONFIG.FILTERS.ENDYEAR")).startOf('year'), end: moment(environment.get("CONFIG.FILTERS.ENDYEAR")) }
                                            : this._date.get((id - 1)));
        }

        return this.date.get(id);
    }

    canSeeGroupClient() {
        return isAuthorized(FeaturePermission.LAYER_CUSTOMER.showGroupClient);
    }

    toggleOverview() {
        const currentMap = worldMap.getCurrent();
        const layer = this.getLayer(currentMap._id);
        const features = layer.getCurrentFeatures();

        if (!store.getters[OVERVIEW_PANEL_IS_VISIBLE] || !_.isEqual(this._overviewFeatures, features))
          api.general.log.action(ContextEnum.LayerCustomer, 'Visualizar Gráfico');

        if ((_.size(features) && !_.isEqual(this._overviewFeatures, features)) || !store.getters[OVERVIEW_PANEL_IS_VISIBLE]) {
            this.summary(features);
            this._overviewFeatures = _.cloneDeep(features);

            store.dispatch(OVERVIEW_PANEL_CHANGE_CURRENT, this.options.name);
        }
    }

    summary(features) {
        const data = this.calculate(features);

        const chartPie = new DataTable(data.perPercentage, {
            row: translate("Common.Name"),
            column: [translate("Common.Total")]
        });

        const chartColumn = new DataTable(data.perTotal, {
            row: translate("Common.Name"),
            column: [translate("Common.Total")]
        });

        const options = {
            title: {
                table: `${environment.get('CONFIG.CLIENT')}_Painel de Dados_Clientes_v${moment().format('YYYYMMDD')}`,
                chartPie: `TOP 10 - ${translate("Common.Dealer")} (%)`,
                chartColumn: `TOP 10 - ${translate("Common.Dealer")}`
            },
            dash: true,
            colors: [
                null,
                _.map(_.range(0, data.perTotal.length), () => "#3498db")
            ],
            data: [data.summary, chartPie, chartColumn],
            convertTable: false,
            format: ["{0:n0}", "{0:p2}", "{0:n0}"],
            columns: [
                {
                    field: "name",
                    title: translate("Common.Name")
                },
                {
                    field: "group",
                    title: translate("Common.Group")
                },
                {
                    field: "total",
                    title: translate("Common.Total"),
                    width: 100,
                    template: data => numeral(data.total).format("0,0")
                },
                {
                    field: "percentage",
                    title: `${translate("Common.Total")} (%)`,
                    width: 100,
                    template: data =>
                        numeral(data.percentage).format("0,0[.]00%")
                }
            ],
            buttons:{
                fullscreen: true,
                export: isAuthorized(FeaturePermission[this.options.context].exportDataPanel)
            }
        };

        if (this.canSeeGroupClient()) {
            options.columns.splice(2, 0, {
                field: 'groupClient',
                title: `${translate('Common.Group')} ${translate('Common.Client')}`
            });
        }

        dataBoard.show(options);
    }

    calculate(features) {
        const stores = _.mapKeys(
            environment.get("CONFIG.FILTERS.DEALERS_STORES"),
            "Id"
        );
        const total = features.length;

        const summary = _(features)
            .groupBy(f => f.properties.IdLoja)
            .map((val, key) => ({
                name: stores[key].Nome,
                group: stores[key].Grupo,
                groupClient: stores[key].GrupoCliente,
                total: val.length,
                percentage: val.length / total
            }))
            .orderBy("total")
            .reverse()
            .value();

        const perPercentage = _(features)
            .groupBy(f => f.properties.IdLoja)
            .map((val, key) => ({
                [translate("Common.Name")]: stores[key].Nome,
                [translate("Common.Total")]: val.length / total
            }))
            .orderBy(translate("Common.Total"))
            .reverse()
            .take(10)
            .value();

        const perTotal = _(features)
            .groupBy(f => f.properties.IdLoja)
            .map((val, key) => ({
                [translate("Common.Name")]: stores[key].Nome,
                [translate("Common.Total")]: val.length
            }))
            .orderBy(translate("Common.Total"))
            .reverse()
            .take(10)
            .value();

        return {
            perPercentage,
            perTotal,
            summary
        };
    }

    setHaloSelected(idLoja) {
        this.selecteds = [...this.selecteds, idLoja];
    }

    removeHaloSelected(idLoja) {
        this.selecteds = this.selecteds.filter(x => x !== idLoja);
    }

    isHaloSelected(idLoja) {
        return this.selecteds.some(x => x === idLoja);
    }

    /**
     * Add new store to the store collection and request customers
     * @param points
     * @param storeLayer
     * @param autoReload
     */
    add(
        points: number | Array<number>,
        fuelTypes: any,
        segments: any,
        saleType: any,
        storeLayer?: GeoJSONLayer,
        autoReload = true
    ) {
        const layer = this.getLayer();
        const { symbology } = this.getLayer().options;

        layer.clear();

        if (!storeLayer) {
            storeLayer = layers.get("Dealerships", worldMap.getCurrent());
        }

        if (!storeLayer.state.customIcons) {
            storeLayer.state.customIcons = [];
        }

        if (typeof points === "number") {
            this._updateFiltersValues(points, this.currentStores);
            symbology.values = symbology.values.filter(v => v.value === points);
            this.currentStores.add(points);
            if(_.size(this.currentStores) === 0){
                points = [points];
            }else{
                points = [];
                this.currentStores.forEach(id => { points.push(id); });
            }
        }

        if (!typeof points === "number" || _.size(this.currentStores) > 0){
            this.currentStores.forEach(store => {
                this._restoreStoreIcon(store, storeLayer);

                _.forEach(layer.getFeatures(), feature => {
                    if (feature && feature.properties.IdLoja === store) {
                        layer.removeLayer(feature.properties.Id, false);
                        symbology.values = symbology.values.filter(v => v.value === store);
                    }
                });
            });
        }

        const queue = [];
        const saleTypeValue = saleType && !_.isArray(saleType.value) ? saleType.value : null;

        _.forEach(points, id => {
            const date = this.getDate();
            const style = this._setStoreIcon(id, storeLayer);

            this.setHaloSelected(id);

            storeLayer.state.customIcons = [
                ...storeLayer.state.customIcons.filter(x => x.idStore !== id),
                { idStore: id, icon: style.store }];

            symbology.values.push(style.customers);
            this.getLayer().setOptions({
                symbology
            });

            queue.push(
                api.fetch({
                    type: "GET",
                    url: `${environment.get("API_HOST")}/lojaconcessionaria/clientes`,
                    data: {
                        IdLoja: id,
                        IdFuelTypes: fuelTypes && fuelTypes.value ? fuelTypes.value.join(",") : null,
                        IdSegments: segments && segments.value ? segments.value.join(",") : null,
                        IdSaleType: saleTypeValue && saleTypeValue !== SaleTypeEnum.Total ? saleTypeValue : null,
                        AnoMesInicio: date.start.format("YYYYMM"),
                        AnoMesFim: date.end.format("YYYYMM")
                    }
                })
            );
        });

        Promise.all(queue.map((promise, i) =>
            promise.catch(err => {
                err.index = i;
                return err;
            })))
            .then(res => {
                const storeData = res.filter(r => r.objects.output && r.objects.output.geometries.length > 0);
                if (storeData.length === 0) {
                    notify(NOTIFY_TYPE.LOG, translate("Message.NotFounded", { resource: "$t(Common.Client)" }));
                    return;
                }

                _.forEach(storeData, featureCollection => {
                    layer.setFeatures(featureCollection, autoReload);
                });
            });
    }

    remove(id: number, storeLayer?: GeoJSONLayer, autoReload = true) {
        const layer = this.getLayer();
        if (!storeLayer) {
            storeLayer = layers.get("Dealerships", worldMap.getCurrent());
        }

        this._restoreStoreIcon(id, storeLayer);
        this._updateFiltersValues(null, this.currentStores);

        _.forEach(layer.getFeatures(), feature => {
            if (feature && feature.properties.IdLoja === id) {
                layer.removeLayer(feature.properties.Id, false);
            }
        });

        this.currentStores.delete(id);

        if (layer.visible() && autoReload) {
            layer.reload();
        }
    }
}

export default CustomersControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/CustomersControl.js